import React, { Fragment } from "react";
import CustomModal from "components/ui-component/CustomModal";
import Drawer from "components/ui-component/SideDrawer";
import AddEditCardForm from "components/paymentMethod/AddEditCardForm";
import AddEditBankForm from "components/paymentMethod/AddEditBankForm";
import AddEditWalletForm from "components/paymentMethod/AddEditWalletForm";
import { useTid } from "utils/testid";

interface Props {
  // modals
  isBtnLoading: boolean;
  wallet: any;
  setWallet: (w: any) => void;
  handleUpdateWallet: () => Promise<void>;
  cardNumber: any;
  setCardNumber: (c: any) => void;
  deleteCard: any;
  setDeleteCard: (c: any) => void;
  onCardDefault: () => Promise<void>;
  onCardDelete: () => Promise<void>;

  // drawers
  isLessThan590: boolean;
  isCardAvailable: boolean;
  isAddCard: boolean;
  setIsAddCard: (b: boolean) => void;
  isEditCard: boolean;
  setIsEditCard: (b: boolean) => void;
  editCardData: any;
  setEditCardData: (d: any) => void;
  onOldCardDataGet: () => Promise<void>;

  isBankAvailable: boolean;
  isAddBank: boolean;
  setIsAddBank: (b: boolean) => void;
  isEditBank: boolean;
  setIsEditBank: (b: boolean) => void;
  editBankData: any;
  setEditBankData: (d: any) => void;

  isAddWallet: boolean;
  setIsAddWallet: (b: boolean) => void;
  isEditWallet: boolean;
  setIsEditWallet: (b: boolean) => void;
  editWalletData: any;
  setEditWalletData: (d: any) => void;
  onOldWalletDataGet: () => Promise<void>;
  isSuccessScreeenWallet: boolean;
  isFailedScreenWallet: boolean;
  isScreenLoading: boolean;
}

const PaymentMethodDialogs: React.FC<Props> = ({
  isBtnLoading,
  wallet,
  setWallet,
  handleUpdateWallet,
  cardNumber,
  setCardNumber,
  deleteCard,
  setDeleteCard,
  onCardDefault,
  onCardDelete,
  isLessThan590,
  isCardAvailable,
  isAddCard,
  setIsAddCard,
  isEditCard,
  setIsEditCard,
  editCardData,
  setEditCardData,
  onOldCardDataGet,
  isBankAvailable,
  isAddBank,
  setIsAddBank,
  isEditBank,
  setIsEditBank,
  editBankData,
  setEditBankData,
  isAddWallet,
  setIsAddWallet,
  isEditWallet,
  setIsEditWallet,
  editWalletData,
  setEditWalletData,
  onOldWalletDataGet,
  isSuccessScreeenWallet,
  isFailedScreenWallet,
  isScreenLoading,
}) => {
  const tid = useTid();

  // Only flip `showModal` when closing. Blanking the card number / wallet name
  // at the same time makes the confirmation sentence read "...ending in ?" for
  // the length of the closing animation, because the panel is still on screen.
  const closeMethodModal = (setItem: (updater: any) => void) => {
    setItem((current: any) => ({ ...current, showModal: false }));
  };

  const closeWalletModal = () => {
    setWallet((current: any) => ({ ...current, showModal: false }));
  };

  return (
    <>
      <CustomModal
        signModal={false}
        isLoading={isBtnLoading}
        isOpen={wallet?.type !== "delete" ? wallet?.showModal : false}
        title="Set As Default"
        data-testid={tid("wallet-default-modal")}
        aria-label="Set wallet as default"
        className="lg:w-[600px] h-[250px] lg:h-[286px] w-[350px]"
        onDoneClick={() => {
          handleUpdateWallet();
        }}
        onCloseClick={() => {
          closeWalletModal();
        }}
      >
        <Fragment>
          <div
            className="pt-5 lg:text-xl text-base text-txtPrimary lg:leading-[30px]"
            data-testid={tid("wallet-default-modal-message")}
          >
            You are about to change your default payment method to{" "}
            <span className="font-bold">{wallet?.method}</span>.
          </div>
          <div className="lg:text-xl text-base text-txtSecondary lg:leading-[30px] pt-3">
            This will be used for all future payments.
          </div>
        </Fragment>
      </CustomModal>

      <CustomModal
        signModal={false}
        isLoading={isBtnLoading}
        isOpen={wallet?.type === "delete" ? wallet?.showModal : false}
        title="Delete"
        doneText="Delete"
        data-testid={tid("wallet-delete-modal")}
        aria-label="Delete wallet payment method"
        className="lg:w-[600px] h-[226px] lg:h-[286px] w-[350px]"
        onDoneClick={() => {
          handleUpdateWallet();
        }}
        onCloseClick={() => closeWalletModal()}
      >
        <Fragment>
          <div
            className="pt-5 lg:text-xl text-base text-txtPrimary lg:leading-[30px]"
            data-testid={tid("wallet-delete-modal-message")}
          >
            Are you sure you want to remove this payment method{" "}
            <span className="font-bold">{wallet?.method}</span>?
          </div>
          <div className="lg:text-xl text-base text-txtSecondary lg:leading-[30px] pt-3">
            This action cannot be undone.
          </div>
        </Fragment>
      </CustomModal>

      <CustomModal
        signModal={false}
        isLoading={isBtnLoading}
        isOpen={cardNumber?.showModal}
        title="Set As Default"
        data-testid={tid("method-default-modal")}
        aria-label="Set payment method as default"
        className="lg:w-[600px] h-[250px] lg:h-[286px] w-[350px]"
        onDoneClick={() => {
          onCardDefault();
        }}
        onCloseClick={() => closeMethodModal(setCardNumber)}
      >
        <Fragment>
          <div
            className="pt-5 lg:text-xl text-base text-txtPrimary lg:leading-[30px]"
            data-testid={tid("method-default-modal-message")}
          >
            You are about to change your default payment method to account
            ending in{" "}
            <span className="font-bold">{cardNumber?.number?.slice(-4)}</span>.
          </div>
          <div className="lg:text-xl text-base text-txtSecondary lg:leading-[30px] pt-3">
            This will be used for all future payments.
          </div>
        </Fragment>
      </CustomModal>

      <CustomModal
        signModal={false}
        isLoading={isBtnLoading}
        isOpen={deleteCard?.showModal}
        title="Delete"
        doneText="Delete"
        data-testid={tid("method-delete-modal")}
        aria-label="Delete payment method"
        className="lg:w-[600px] h-[226px] lg:h-[286px] w-[350px]"
        onDoneClick={() => {
          onCardDelete();
        }}
        onCloseClick={() => {
          closeMethodModal(setDeleteCard);
        }}
      >
        <Fragment>
          <div
            className="pt-5 lg:text-xl text-base text-txtPrimary lg:leading-[30px]"
            data-testid={tid("method-delete-modal-message")}
          >
            Are you sure you want to remove this payment method ending in{" "}
            <span className="font-bold">{deleteCard?.number?.slice(-4)}</span>?
          </div>
          <div className="lg:text-xl text-base text-txtSecondary lg:leading-[30px] pt-3">
            This action cannot be undone.
          </div>
        </Fragment>
      </CustomModal>

      {isCardAvailable && (
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isAddCard}
          size={isLessThan590 ? "100vw" : "590px"}
          onClose={() => {
            setIsAddCard(false);
            setEditCardData(null);
            setIsEditCard(false);
          }}
          overlayColor="#000"
          className="h-full"
        >
          <div
            className="contents"
            data-testid={tid(
              isEditCard ? "edit-card-drawer" : "add-card-drawer"
            )}
          >
            <AddEditCardForm
              show={isAddCard}
              setShow={setIsAddCard}
              isEdit={isEditCard}
              setIsEdit={setIsEditCard}
              onCloseClick={() => onOldCardDataGet()}
              data={editCardData}
              setData={setEditCardData}
            />
          </div>
        </Drawer>
      )}

      {isBankAvailable && (
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isAddBank}
          size={isLessThan590 ? "100vw" : "590px"}
          onClose={() => {
            setIsAddBank(false);
            setEditBankData(null);
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid(
              isEditBank ? "edit-bank-drawer" : "add-bank-drawer"
            )}
          >
            <AddEditBankForm
              show={isAddBank}
              setShow={setIsAddBank}
              isEdit={isEditBank}
              setIsEdit={setIsEditBank}
              onCloseClick={() => onOldCardDataGet()}
              data={editBankData}
              setData={setEditBankData}
            />
          </div>
        </Drawer>
      )}

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isAddWallet}
        size={isLessThan590 ? "100vw" : "590px"}
        onClose={() => {
          setIsAddWallet(false);
          setEditWalletData(null);
        }}
        overlayColor="#000"
      >
        <div
          data-testid={tid(
            isEditWallet ? "edit-wallet-drawer" : "add-wallet-drawer"
          )}
        >
          <AddEditWalletForm
            show={isAddWallet}
            setShow={setIsAddWallet}
            isEdit={isEditWallet}
            setIsEdit={setIsEditWallet}
            onCloseClick={() => onOldWalletDataGet()}
            data={editWalletData}
            setData={setEditWalletData}
            isSuccess={isSuccessScreeenWallet}
            isFailed={isFailedScreenWallet}
            isScreenLoading={isScreenLoading}
          />
        </div>
      </Drawer>
    </>
  );
};

export default PaymentMethodDialogs;
